'use client';

import React from 'react';
import { useFormContext } from 'react-hook-form';
import { CalendarIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns';

import { cn } from '@/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';

const getDisabledDates = (type) => {
  switch (type) {
    case 'past':
      return (date) => date < new Date();
    case 'future':
      return (date) => date > new Date();
    case 'all':
    default:
      return undefined;
  }
};

const DatePicker = ({ name, label, description, disabledDates = 'all' }) => {
  const form = useFormContext();
  const [month, setMonth] = React.useState(new Date());

  const years = Array.from(
    { length: 66 }, // Add 1 to include current year
    (_, i) => new Date().getFullYear() - 65 + i // Start 65 years ago and go up to current year
  );
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col w-full">
          <FormLabel>{label}</FormLabel>
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant={'outline'}
                  className={cn(
                    'w-full pl-3 text-left font-normal flex items-center justify-start',
                    !field.value && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="flex-grow text-left truncate">
                    {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                  </span>
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="flex gap-2 p-3">
                <Select
                  onValueChange={(value) =>
                    setMonth(
                      (prev) => new Date(prev.getFullYear(), parseInt(value))
                    )
                  }
                >
                  <SelectTrigger className="w-[110px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month, index) => (
                      <SelectItem key={month} value={index.toString()}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  onValueChange={(value) =>
                    setMonth(
                      (prev) => new Date(parseInt(value), prev.getMonth())
                    )
                  }
                >
                  <SelectTrigger className="w-[90px]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Calendar
                mode="single"
                selected={field.value}
                onSelect={field.onChange}
                month={month}
                onMonthChange={setMonth}
                disabled={getDisabledDates(disabledDates)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default DatePicker;
